import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Player from '@vimeo/player';

const VideoPlayer = () => {
  const { courseId, videoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [videoData, setVideoData] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [quality, setQuality] = useState('auto');
  const [showControls, setShowControls] = useState(true);

  // Mock video data - in real app, this would come from an API
  const mockVideos = {
    '1': {
      id: '1',
      title: 'Introduction to React Hooks',
      description: 'Learn the fundamentals of React Hooks and how to use them in your applications.',
      vimeoId: '76979871',
      duration: '15:30',
      thumbnail: 'https://i.vimeocdn.com/video/452001743_640.jpg',
      course: 'React Masterclass'
    },
    '2': {
      id: '2',
      title: 'Advanced JavaScript Patterns',
      description: 'Deep dive into advanced JavaScript patterns and best practices.',
      vimeoId: '19231868',
      duration: '22:15',
      thumbnail: 'https://i.vimeocdn.com/video/121375553_640.jpg',
      course: 'JavaScript Pro'
    },
    '3': {
      id: '3',
      title: 'CSS Grid Layout Mastery',
      description: 'Master CSS Grid Layout with practical examples and real-world projects.',
      vimeoId: '148751763',
      duration: '18:45',
      thumbnail: 'https://i.vimeocdn.com/video/550310345_640.jpg',
      course: 'CSS Advanced'
    }
  };

  // Get video data from URL params or location state
  useEffect(() => {
    const videoFromState = location.state?.video;
    const videoFromParams = videoId ? mockVideos[videoId] : null;
    
    setVideoData(videoFromState || videoFromParams || mockVideos['1']);
    setIsLoading(false);
  }, [videoId, location.state]);

  // Initialize Vimeo player
  useEffect(() => {
    if (!videoData || !iframeRef.current) return;

    const initializePlayer = async () => {
      try {
        const newPlayer = new Player(iframeRef.current, {
          id: parseInt(videoData.vimeoId),
          width: 1280,
          height: 720,
          responsive: true,
          quality: quality,
          playsinline: true,
          transparent: false,
          speed: true,
          title: true,
          byline: true,
          portrait: true,
          background: false
        });

        setPlayer(newPlayer);

        // Player events
        newPlayer.on('play', () => setIsPlaying(true));
        newPlayer.on('pause', () => setIsPlaying(false));
        newPlayer.on('timeupdate', (data) => setCurrentTime(data.seconds));
        newPlayer.on('loaded', () => setIsLoading(false));
        newPlayer.on('durationchange', (data) => setDuration(data.duration));
        newPlayer.on('volumechange', (data) => setVolume(data.volume));
        newPlayer.on('playbackratechange', (data) => setPlaybackRate(data.playbackRate));
        newPlayer.on('ended', () => handleVideoEnd());

        await newPlayer.ready();
        console.log('Player is ready');

      } catch (error) {
        console.error('Error initializing player:', error);
        setIsLoading(false);
      }
    };

    initializePlayer();

    return () => {
      if (player) {
        player.destroy();
      }
    };
  }, [videoData, quality]);

  // Controls visibility
  useEffect(() => {
    let timeout;
    if (isPlaying) {
      timeout = setTimeout(() => setShowControls(false), 3000);
    }
    return () => clearTimeout(timeout);
  }, [isPlaying, showControls]);

  const handlePlayPause = async () => {
    if (!player) return;
    
    try {
      if (isPlaying) {
        await player.pause();
      } else {
        await player.play();
      }
    } catch (error) {
      console.error('Error playing/pausing:', error);
    }
  };

  const handleSeek = async (time) => {
    if (!player) return;
    
    try {
      await player.setCurrentTime(time);
    } catch (error) {
      console.error('Error seeking:', error);
    }
  };

  const handleVolumeChange = async (newVolume) => {
    if (!player) return;
    
    try {
      await player.setVolume(newVolume);
      setVolume(newVolume);
    } catch (error) {
      console.error('Error changing volume:', error);
    }
  };

  const handlePlaybackRateChange = async (rate) => {
    if (!player) return;
    
    try {
      await player.setPlaybackRate(rate);
      setPlaybackRate(rate);
    } catch (error) {
      console.error('Error changing playback rate:', error);
    }
  };

  const handleQualityChange = (newQuality) => {
    setQuality(newQuality);
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleFullscreen = () => {
    if (!player) return;
    
    player.requestFullscreen();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-900 text-lg">Loading video...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 text-gray-900">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{videoData?.title}</h1>
              <p className="text-gray-600 text-sm">{videoData?.course}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Main Video Player */}
        <div className="flex-1 p-4 lg:p-6">
          <div 
            className="relative bg-black rounded-2xl overflow-hidden max-w-6xl mx-auto shadow-2xl"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => isPlaying && setShowControls(false)}
          >
            {/* Video Player */}
            <div ref={playerRef} className="w-full aspect-video">
              <div 
                ref={iframeRef}
                id="vimeo-player"
                className="w-full h-full"
              />
            </div>

            {/* Custom Controls Overlay */}
            {showControls && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-6 transition-opacity duration-300">
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div 
                    className="w-full bg-gray-600 h-1 rounded-full cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      handleSeek(percent * duration);
                    }}
                  >
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 h-1 rounded-full transition-all duration-200"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-300 mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>

                {/* Control Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Play/Pause */}
                    <button
                      onClick={handlePlayPause}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors duration-200 text-white"
                    >
                      {isPlaying ? (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
                        </svg>
                      ) : (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </button>

                    {/* Volume */}
                    <div className="flex items-center space-x-2">
                      <button className="p-1 hover:bg-white/10 rounded-full text-white">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                        </svg>
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={volume}
                        onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                        className="w-20 accent-green-500"
                      />
                    </div>

                    {/* Time */}
                    <span className="text-sm font-mono text-white">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* Playback Speed */}
                    <select
                      value={playbackRate}
                      onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
                      className="bg-gray-800 border border-gray-600 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value={0.25}>0.25x</option>
                      <option value={0.5}>0.5x</option>
                      <option value={0.75}>0.75x</option>
                      <option value={1}>1x</option>
                      <option value={1.25}>1.25x</option>
                      <option value={1.5}>1.5x</option>
                      <option value={2}>2x</option>
                    </select>

                    {/* Quality */}
                    <select
                      value={quality}
                      onChange={(e) => handleQualityChange(e.target.value)}
                      className="bg-gray-800 border border-gray-600 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="auto">Auto</option>
                      <option value="360p">360p</option>
                      <option value="540p">540p</option>
                      <option value="720p">720p</option>
                      <option value="1080p">1080p</option>
                    </select>

                    {/* Fullscreen */}
                    <button
                      onClick={handleFullscreen}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors duration-200 text-white"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-black flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-white">Loading video...</p>
                </div>
              </div>
            )}
          </div>

          {/* Video Info */}
          <div className="max-w-6xl mx-auto mt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{videoData?.title}</h2>
            <p className="text-gray-600 mb-4">{videoData?.description}</p>
            
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span>Duration: {videoData?.duration}</span>
              <span>•</span>
              <span>Course: {videoData?.course}</span>
            </div>
          </div>
        </div>

        {/* Course Navigation Sidebar */}
        <div className="w-full lg:w-80 bg-white border-l border-gray-200 p-6 overflow-y-auto">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Course Content</h3>
          
          <div className="space-y-3">
            {Object.values(mockVideos).map((video, index) => (
              <div
                key={video.id}
                onClick={() => setVideoData(video)}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-300 border-2 ${
                  videoData?.id === video.id 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg transform scale-105' 
                    : 'bg-white border-gray-200 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:border-green-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    videoData?.id === video.id 
                      ? 'bg-white text-green-600 shadow-lg' 
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate transition-colors duration-300 ${
                      videoData?.id === video.id ? 'text-white' : 'text-gray-700'
                    }`}>
                      {video.title}
                    </p>
                    <p className={`text-xs transition-colors duration-300 ${
                      videoData?.id === video.id ? 'text-green-100' : 'text-gray-500'
                    }`}>
                      {video.duration}
                    </p>
                  </div>
                  {videoData?.id === video.id && (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;